import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { LANGUAGES, CEFR_LEVELS } from '@/lib/languages'
import { analytics, setUserProperties } from '@/lib/analytics'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { DeleteAccountModal } from '@/components/ui/delete-account-modal'
import type { CEFRLevel } from '@/types/database'
import { ArrowLeft, AlertTriangle } from 'lucide-react'

export function Settings() {
  const navigate = useNavigate()
  const { profile, updateProfile, deleteAccount } = useAuth()
  const [name, setName] = useState(profile?.name || '')
  const [nativeLanguage, setNativeLanguage] = useState(profile?.native_language || 'en')
  const [targetLanguage, setTargetLanguage] = useState(profile?.target_language || 'es')
  const [level, setLevel] = useState<CEFRLevel>(profile?.level || 'A1')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  const handleSave = async () => {
    if (nativeLanguage === targetLanguage) {
      setError('Native and target language must be different')
      return
    }

    setIsLoading(true)
    setError(null)
    setSaved(false)

    const { error } = await updateProfile({
      name: name.trim() || null,
      native_language: nativeLanguage,
      target_language: targetLanguage,
      level,
    })

    if (error) {
      setError(error.message)
    } else {
      // Track which fields changed
      const changedFields: string[] = []
      if (name.trim() !== (profile?.name || '')) changedFields.push('name')
      if (nativeLanguage !== profile?.native_language) changedFields.push('native_language')
      if (targetLanguage !== profile?.target_language) changedFields.push('target_language')
      if (level !== profile?.level) changedFields.push('level')

      if (changedFields.length > 0) {
        analytics.settingsChanged(changedFields)
        // Update user properties in analytics
        setUserProperties({
          name: name.trim() || null,
          native_language: nativeLanguage,
          target_language: targetLanguage,
          cefr_level: level,
        })
      }

      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
    }
    setIsLoading(false)
  }

  const availableTargetLanguages = LANGUAGES.filter(l => l.code !== nativeLanguage)

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-gradient-to-r from-amber-50/60 via-white to-orange-50/60 border-b border-amber-200/30 shadow-sm">
        <div className="container mx-auto px-4 py-3 md:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 md:gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/')}>
              <ArrowLeft className="h-4 w-4 mr-1" />
              <span className="hidden sm:inline">Back</span>
            </Button>
            <h1 className="text-lg md:text-xl font-semibold text-gray-800">Settings</h1>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <div className="max-w-xl mx-auto space-y-6">
          <div className="bg-gradient-to-br from-amber-50/80 via-white to-orange-50/80 rounded-2xl border border-amber-200/50 shadow-lg overflow-hidden">
            <div className="h-1.5 bg-gradient-to-r from-amber-400 via-orange-400 to-rose-400" />
            <div className="p-4 md:p-6">
              <h3 className="text-lg font-semibold text-gray-800">Profile</h3>
              <p className="text-sm text-gray-600 mb-4">
                Your display name - the tutor will use this to address you
              </p>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-xs font-medium text-amber-700/70 uppercase tracking-wide">Name</Label>
                  <Input
                    id="name"
                    placeholder="Enter your name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    maxLength={100}
                    className="border-amber-200/50 focus:border-amber-400 focus:ring-amber-400/20"
                  />
                </div>

                {/* Email (read-only) */}
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-amber-700/70 uppercase tracking-wide">
                    Email
                  </Label>
                  <div className="px-3 py-2 bg-gray-50 border border-amber-200/50 rounded-lg text-gray-600 text-sm">
                    {profile?.email || '—'}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-amber-50/80 via-white to-orange-50/80 rounded-2xl border border-amber-200/50 shadow-lg overflow-hidden">
            <div className="h-1.5 bg-gradient-to-r from-amber-400 via-orange-400 to-rose-400" />
            <div className="p-4 md:p-6">
              <h3 className="text-lg font-semibold text-gray-800">Language Settings</h3>
              <p className="text-sm text-gray-600 mb-4">
                Update your learning preferences
              </p>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-amber-700/70 uppercase tracking-wide">Native Language</Label>
                  <SearchableSelect
                    value={nativeLanguage}
                    onValueChange={setNativeLanguage}
                    options={LANGUAGES.map((lang) => ({
                      value: lang.code,
                      label: `${lang.name} (${lang.nativeName})`,
                      searchTerms: [lang.name, lang.nativeName],
                    }))}
                    placeholder="Select your native language"
                    searchPlaceholder="Search languages..."
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-medium text-amber-700/70 uppercase tracking-wide">Target Language</Label>
                  <SearchableSelect
                    value={targetLanguage}
                    onValueChange={setTargetLanguage}
                    options={availableTargetLanguages.map((lang) => ({
                      value: lang.code,
                      label: `${lang.name} (${lang.nativeName})`,
                      searchTerms: [lang.name, lang.nativeName],
                    }))}
                    placeholder="Select target language"
                    searchPlaceholder="Search languages..."
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-amber-50/80 via-white to-orange-50/80 rounded-2xl border border-amber-200/50 shadow-lg overflow-hidden">
            <div className="h-1.5 bg-gradient-to-r from-amber-400 via-orange-400 to-rose-400" />
            <div className="p-4 md:p-6">
              <h3 className="text-lg font-semibold text-gray-800">Proficiency Level</h3>
              <p className="text-sm text-gray-600 mb-4">
                Your current CEFR level. The AI will adapt based on your actual performance.
              </p>
              <div className="grid gap-2">
                {CEFR_LEVELS.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => setLevel(l.code as CEFRLevel)}
                    className={`p-4 text-left rounded-xl border transition-colors ${
                      level === l.code
                        ? 'border-amber-400 bg-amber-50/80 shadow-sm'
                        : 'border-amber-200/50 hover:border-amber-300 hover:bg-amber-50/40'
                    }`}
                  >
                    <div className="font-medium text-gray-800">
                      {l.code === 'A0' ? l.name : `${l.code} - ${l.name}`}
                    </div>
                    <div className="text-sm text-gray-600">
                      {l.description}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {error && (
            <p className="text-sm text-rose-600">{error}</p>
          )}

          {saved && (
            <p className="text-sm text-emerald-600">Settings saved successfully!</p>
          )}

          <Button
            className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-lg shadow-amber-500/25 hover:shadow-xl hover:shadow-amber-500/30 transition-all"
            onClick={handleSave}
            disabled={isLoading}
          >
            {isLoading ? 'Saving...' : 'Save Changes'}
          </Button>

          {/* Danger Zone */}
          <div className="bg-gradient-to-br from-rose-50/80 via-white to-red-50/80 rounded-2xl border border-rose-200/50 shadow-lg overflow-hidden mt-8">
            <div className="h-1.5 bg-gradient-to-r from-rose-400 via-red-400 to-rose-500" />
            <div className="p-4 md:p-6">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="h-5 w-5 text-rose-500" />
                <h3 className="text-lg font-semibold text-gray-800">Danger Zone</h3>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Permanently delete your account and all associated data. This action cannot be undone.
              </p>
              <Button
                variant="outline"
                onClick={() => setShowDeleteModal(true)}
                className="border-rose-300 text-rose-600 hover:bg-rose-50 hover:text-rose-700 hover:border-rose-400"
              >
                Delete Account
              </Button>
            </div>
          </div>
        </div>
      </main>

      <DeleteAccountModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={async () => {
          const result = await deleteAccount()
          if (!result.error) {
            navigate('/')
          }
          return result
        }}
      />
    </div>
  )
}
